import * as path from 'path';
import * as fs from 'fs-extra';
import { pathToFileURL } from 'url';

export interface CustomRoute {
	path: string;
	name: string;
	redirect?: string | ((to: any) => string);
	component?: string;
	meta?: Record<string, any>;
	props?: boolean | Record<string, any>;
	beforeEnter?: (to: any, from: any, next: any) => void;
}

export interface FrameConfig {
	// Vite configuration overrides
	vite?: {
		plugins?: any[];
		build?: any;
		server?: any;
		resolve?: any;
		css?: {
			preprocessorOptions?: {
				less?: any;
				scss?: any;
				sass?: any;
			};
			postcss?: any;
		};
	};

	// Routing configuration
	routing?: {
		viewsDir?: string;
		routePrefix?: string;
		generateTypes?: boolean;
		customRoutes?: CustomRoute[];
	};

	// Build configuration
	build?: {
		outDir?: string;
		sourcemap?: boolean;
		minify?: boolean;
	};

	// Development configuration
	dev?: {
		port?: number;
		host?: string;
		open?: boolean;
		ignore?: string[]; // Patterns to ignore in file watching
	};

	// Asset configuration
	assets?: {
		publicDir?: string;
		assetsDir?: string;
		generateHelpers?: boolean;
		optimize?: boolean;
		formats?: string[];
	};

	// Development tools configuration
	devTools?: {
		hmr?: boolean;
		overlay?: boolean;
		eslint?: boolean;
		prettier?: boolean;
		testing?: {
			framework?: 'vitest' | 'jest';
			coverage?: boolean;
			ui?: boolean;
		};
		performance?: {
			bundleAnalyzer?: boolean;
			lighthouse?: boolean;
		};
	};

	// Template configuration
	templates?: {
		pug?: {
			enabled?: boolean;
			options?: {
				pretty?: boolean;
				filters?: Record<string, any>;
				globals?: Record<string, any>;
				cache?: boolean;
				inlineRuntimeFunctions?: boolean;
				compileDebug?: boolean;
			};
		};
	};

	// Meta and SEO configuration
	meta?: {
		enabled?: boolean;
		defaults?: {
			title?: string;
			titleTemplate?: string;
			description?: string;
			keywords?: string[];
			author?: string;
			viewport?: string;
			charset?: string;
			themeColor?: string;

			// Open Graph
			openGraph?: {
				siteName?: string;
				type?: string;
				locale?: string;
				image?: string;
				imageWidth?: number;
				imageHeight?: number;
				imageAlt?: string;
				url?: string;
			};

			// Twitter Card
			twitter?: {
				card?: string;
				site?: string;
				creator?: string;
				image?: string;
				imageAlt?: string;
			};

			// Additional meta tags
			meta?: Array<{
				name?: string;
				property?: string;
				content: string;
				key?: string;
			}>;
		};
	};
}

export class ConfigLoader {
	private cwd: string;
	private config: FrameConfig = {};

	constructor(cwd: string) {
		this.cwd = cwd;
	}

	async load(): Promise<FrameConfig> {
		// Load configuration in order of precedence (last wins)
		await this.loadDefaults();
		await this.loadProjectConfig();

		return this.config;
	}

	private async loadDefaults(): Promise<void> {
		this.config = {
			routing: {
				viewsDir: 'src/views',
				routePrefix: '',
				generateTypes: true,
			},
			build: {
				outDir: 'dist',
				sourcemap: true,
				minify: true,
			},
			dev: {
				port: 3000,
				host: 'localhost',
				open: false,
				ignore: ['**/@api/**'], // Default patterns to ignore in file watching
			},
			assets: {
				publicDir: 'public',
				assetsDir: 'src/assets',
				generateHelpers: true,
				optimize: true,
				formats: ['webp', 'avif'],
			},
			devTools: {
				hmr: true,
				overlay: true,
				eslint: false,
				prettier: false,
				testing: {
					framework: 'vitest',
					coverage: false,
					ui: false,
				},
				performance: {
					bundleAnalyzer: false,
					lighthouse: false,
				},
			},
			templates: {
				pug: {
					enabled: true,
					options: {
						pretty: true,
						cache: false,
						compileDebug: true,
					},
				},
			},
			meta: {
				enabled: true,
				defaults: {
					title: 'Frame App',
					titleTemplate: '%s | Frame App',
					description: 'A modern Vue.js application built with Frame',
					keywords: ['vue', 'javascript', 'typescript', 'frame'],
					author: 'Frame Developer',
					viewport: 'width=device-width, initial-scale=1.0',
					charset: 'utf-8',
					themeColor: '#42b883',

					openGraph: {
						siteName: 'Frame App',
						type: 'website',
						locale: 'en_US',
						imageWidth: 1200,
						imageHeight: 630,
					},

					twitter: {
						card: 'summary_large_image',
						site: '@frameapp',
						creator: '@frameapp',
					}
				}
			},
		};
	}

	private async loadProjectConfig(): Promise<void> {
		const configFiles = [
			'frame.config.js',
			'frame.config.mjs',
			'frame.config.ts',
		];

		for (const configFile of configFiles) {
			const configPath = path.join(this.cwd, configFile);

			if (await fs.pathExists(configPath)) {
				try {
					const userConfig = await this.importConfig(configPath);
					this.mergeConfig(userConfig);
					console.log(`[Frame Config] Loaded config from ${configFile}`);
					break;
				} catch (error) {
					console.warn(`[Frame Config] Failed to load config from ${configFile}:`, error);
				}
			}
		}
	}

	private async importConfig(configPath: string): Promise<FrameConfig> {
		try {
			// Convert path to file URL for ES module import
			const fileUrl = pathToFileURL(configPath).href;

			// Add cache-busting parameter to reload the module
			const cacheBustingUrl = `${fileUrl}?t=${Date.now()}`;

			const module = await import(cacheBustingUrl);
			return module.default || module;
		} catch (error) {
			throw new Error(`Failed to import config file ${configPath}: ${error}`);
		}
	}

	private mergeConfig(userConfig: FrameConfig): void {
		// Deep merge user config with defaults
		this.config = this.deepMerge(this.config, userConfig);
	}

	private deepMerge(target: any, source: any): any {
		const result = { ...target };

		for (const key in source) {
			if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
				result[key] = this.deepMerge(target[key] || {}, source[key]);
			} else {
				result[key] = source[key];
			}
		}

		return result;
	}

	get(): FrameConfig {
		return this.config;
	}
} 